import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import {
  Trash2,
  Calendar,
  X,
  MoreVertical,
  RotateCcw,
  ArrowLeft,
  Inbox,
  ChevronUp,
  ChevronDown,
  Eye,
  Building2,
  Mail,
  Phone,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import SenderIdShimmerComponent from "../../components/Senders/SenderIdShimmerComponent";
import LoadErrorPlaceholder from "../../components/LoadErrorPlaceholder";
import useTrashedDeals from "../../hooks/useTrashedDeals";
import RestoreDealModal from "../../components/Deals/RestoreDealModal";
import PermanentDeleteModal from "../../components/Deals/PermanentDeleteModal";
import ViewDealModal from "../../components/Deals/ViewDealModal";

/* ─── Helpers ─────────────────────────────────────────────── */
const SORT_FIELDS = {
  title: "title",
  offer_type: "offer_type",
  price: "discounted_price",
  deleted_at: "deleted_at",
  created_at: "created_at",
};

const statusConfig = {
  Approved: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Approved" },
  pending: { bg: "bg-amber-100", text: "text-amber-800", label: "Pending" },
  Pending: { bg: "bg-amber-100", text: "text-amber-800", label: "Pending" },
  Rejected: { bg: "bg-rose-100", text: "text-rose-800", label: "Rejected" },
  rejected: { bg: "bg-rose-100", text: "text-rose-800", label: "Rejected" },
  expired: { bg: "bg-red-100", text: "text-red-700", label: "Expired" },
  archived: { bg: "bg-slate-100", text: "text-slate-700", label: "Archived" },
};

const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] ?? {
    bg: "bg-gray-100",
    text: "text-gray-700",
    label: status ?? "—",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}
    >
      {cfg.label}
    </span>
  );
};

const offerTypeLabelMap = {
  discount: "Discount",
  buy_one_get_one: "BOGO",
  free_gift: "Free Gift",
  cashback: "Cashback",
  bundle: "Bundle",
  flash_sale: "Flash Sale",
};

const OfferTypeBadge = ({ type }) => {
  const label = offerTypeLabelMap[type] ?? type ?? "—";
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-[#FF6C6C]/10 text-[#CC2E2E] border border-[#FF6C6C]/20">
      {label}
    </span>
  );
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "N/A";
  return new Intl.NumberFormat("en-GH", {
    style: "currency",
    currency: "GHS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(amount)
    .replace("GHS", "GH₵");
};

const getCompanyInitials = (name) => {
  if (!name || typeof name !== "string") return "—";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

/** Vendor / company cell with logo, name, and optional contact */
const VendorCell = ({ deal }) => {
  const logo = deal?.company_logo;
  const name = deal?.company_name;
  const email = deal?.contact?.email;
  const phone = deal?.contact?.phone_number;
  const hasContact = email || phone;
  const [logoFailed, setLogoFailed] = useState(false);
  const showInitials = !logo || logoFailed;

  return (
    <td className="px-5 py-4 align-top">
      <div className="flex items-start gap-3 min-w-0 max-w-[220px]">
        <div className="relative shrink-0 w-11 h-11 rounded-xl bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center ring-1 ring-gray-100">
          {logo && !logoFailed && (
            <img
              src={logo}
              alt={name || "Vendor"}
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => setLogoFailed(true)}
            />
          )}
          <span
            className={`text-xs font-bold text-gray-500 ${showInitials ? "" : "invisible"}`}
            style={{ lineHeight: 1 }}
          >
            {getCompanyInitials(name)}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-800 truncate" title={name}>
            {name || "—"}
          </p>
          {hasContact && (
            <div className="mt-1 space-y-0.5">
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-[#CC2E2E] truncate"
                  title={email}
                >
                  <Mail className="w-3 h-3 shrink-0 opacity-70" />
                  <span className="truncate">{email}</span>
                </a>
              )}
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-[#CC2E2E] truncate"
                  title={phone}
                >
                  <Phone className="w-3 h-3 shrink-0 opacity-70" />
                  <span className="truncate">{phone}</span>
                </a>
              )}
            </div>
          )}
          {!name && !hasContact && (
            <p className="text-xs text-gray-400 mt-0.5">Vendor info n/a</p>
          )}
        </div>
      </div>
    </td>
  );
};

/* ─── Action Menu per row ───────────────────────────────────── */
const TrashedActionMenu = ({ deal, onView, onRestore, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, bottom: undefined });
  const triggerRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !open) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const padding = 8;
    const estimatedMenuHeight = 180;
    const spaceBelow = window.innerHeight - rect.bottom;
    const isDropup = spaceBelow < estimatedMenuHeight && rect.top > padding;
    const left = Math.min(rect.left, window.innerWidth - 192);
    setMenuPos({
      top: !isDropup ? rect.bottom + padding : undefined,
      bottom: isDropup ? window.innerHeight - rect.top + padding : undefined,
      left,
    });
  }, [open]);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        open &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        !e.target.closest(".trashed-action-menu-portal")
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        aria-label="Open actions"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {open &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -4 }}
              transition={{ duration: 0.12 }}
              className="trashed-action-menu-portal fixed z-[9999] w-48 bg-white rounded-xl border border-gray-200 shadow-xl py-1.5 overflow-hidden"
              style={{
                top: menuPos.top,
                bottom: menuPos.bottom,
                left: menuPos.left,
              }}
            >
              <button
                onClick={() => {
                  setOpen(false);
                  onView(deal);
                }}
                className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
              >
                <Eye className="w-4 h-4 shrink-0 text-blue-500" />
                View details
              </button>
              <div className="my-1 border-t border-gray-100" />
              <button
                onClick={() => {
                  setOpen(false);
                  onRestore(deal);
                }}
                className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-left text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
              >
                <RotateCcw className="w-4 h-4 shrink-0 text-emerald-600" />
                Restore Deal
              </button>
              <div className="my-1 border-t border-gray-100" />
              <button
                onClick={() => {
                  setOpen(false);
                  onDelete(deal);
                }}
                className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-left text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4 shrink-0" />
                Delete Forever
              </button>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </>
  );
};

/* ─── Sort Header ───────────────────────────────────────────── */
const SortableHeader = ({ label, sortKey, currentSortBy, currentSortOrder, onSort }) => {
  const isActive = currentSortBy === SORT_FIELDS[sortKey];
  return (
    <button
      onClick={() => onSort(sortKey)}
      className={`flex items-center gap-1 font-semibold text-xs uppercase tracking-wider transition-colors whitespace-nowrap group ${
        isActive ? "text-[#CC2E2E]" : "text-gray-500 hover:text-gray-700"
      }`}
    >
      {label}
      <span className="flex flex-col ml-0.5">
        <ChevronUp
          className={`w-3 h-3 -mb-0.5 transition-colors ${
            isActive && currentSortOrder === "asc" ? "text-[#CC2E2E]" : "text-gray-300 group-hover:text-gray-400"
          }`}
        />
        <ChevronDown
          className={`w-3 h-3 transition-colors ${
            isActive && currentSortOrder === "desc" ? "text-[#CC2E2E]" : "text-gray-300 group-hover:text-gray-400"
          }`}
        />
      </span>
    </button>
  );
};

/* ─── Main Page ─────────────────────────────────────────────── */
const TrashedDeals = () => {
  const {
    deals,
    loading,
    error,
    pagination,
    filters,
    debouncedSearch,
    changePage,
    changeItemsPerPage,
    updateDateFilter,
    updateSort,
    clearDateFilter,
    refreshData,
  } = useTrashedDeals();

  /* Date filter dropdown */
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateDropdownPosition, setDateDropdownPosition] = useState({ top: 0, left: 0 });
  const dateFilterTriggerRef = useRef(null);

  /* Action modal state */
  const [viewModal, setViewModal] = useState({ isOpen: false, deal: null });
  const [restoreModal, setRestoreModal] = useState({ isOpen: false, deal: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, deal: null });

  /* Click-outside date dropdown */
  useEffect(() => {
    const handle = (e) => {
      if (
        showDateFilter &&
        !e.target.closest(".trash-date-filter-container") &&
        !e.target.closest(".trash-date-filter-portal")
      ) {
        setShowDateFilter(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [showDateFilter]);

  const updateDateDropdownPosition = useCallback(() => {
    if (!dateFilterTriggerRef.current) return;
    const rect = dateFilterTriggerRef.current.getBoundingClientRect();
    const dropdownWidth = Math.min(320, window.innerWidth - 24);
    let left = rect.left;
    if (left + dropdownWidth > window.innerWidth - 12) {
      left = window.innerWidth - dropdownWidth - 12;
    }
    setDateDropdownPosition({ top: rect.bottom + 8, left });
  }, []);

  useLayoutEffect(() => {
    if (!showDateFilter) return;
    updateDateDropdownPosition();
  }, [showDateFilter, updateDateDropdownPosition]);

  useEffect(() => {
    if (!showDateFilter) return;
    window.addEventListener("scroll", updateDateDropdownPosition, true);
    window.addEventListener("resize", updateDateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDateDropdownPosition, true);
      window.removeEventListener("resize", updateDateDropdownPosition);
    };
  }, [showDateFilter, updateDateDropdownPosition]);

  /* Sort handler */
  const handleSort = useCallback(
    (headerKey) => {
      const sortBy = SORT_FIELDS[headerKey] || "deleted_at";
      const nextOrder =
        filters.sortBy === sortBy && filters.sortOrder === "desc" ? "asc" : "desc";
      updateSort(sortBy, nextOrder);
    },
    [filters.sortBy, filters.sortOrder, updateSort]
  );

  /* Pagination helpers */
  const { currentPage, totalPages, totalItems, itemsPerPage } = pagination;
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;

  /* Animation variants */
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
  };
  const itemVariants = {
    hidden: { y: 16, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };
  const rowVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar page="Deal Trash Bin" />

      <div className="p-3 sm:p-4 md:p-6">
        {loading && deals.length === 0 ? (
          <SenderIdShimmerComponent />
        ) : error && deals.length === 0 ? (
          <LoadErrorPlaceholder />
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="space-y-5"
          >
            {/* ── Hero Banner ───────────────────────────────────── */}
            <motion.div
              variants={itemVariants}
              className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#CC2E2E] via-[#B52828] to-[#8B1A1A] p-6 shadow-lg"
            >
              {/* Background decoration */}
              <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
              <div className="absolute -bottom-10 -left-6 w-36 h-36 rounded-full bg-white/5" />
              <div className="absolute top-2 right-20 w-20 h-20 rounded-full bg-white/5" />

              <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex shrink-0 items-center justify-center w-14 h-14 rounded-2xl bg-white/15 border border-white/20">
                    <Trash2 className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-white leading-tight">
                      Deal Trash Bin
                    </h1>
                    <p className="text-sm text-[#FFAAAA] mt-0.5 max-w-sm leading-relaxed">
                      Review, restore, or permanently remove deals that have been trashed.
                    </p>
                    <div className="flex items-center gap-3 mt-2.5">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-xs font-semibold text-white">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF6C6C]/60" />
                        {totalItems} {totalItems === 1 ? "deal" : "deals"} in trash
                      </span>
                    </div>
                  </div>
                </div>
                <Link
                  to="/deals"
                  className="self-start sm:self-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/15 border border-white/25 text-sm font-semibold text-white hover:bg-white/25 transition-colors shrink-0"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Deals
                </Link>
              </div>
            </motion.div>

            {/* ── Table Card ────────────────────────────────────── */}
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Filter Bar */}
              <div className="flex flex-col gap-3 p-3 border-b border-gray-100 sm:flex-row sm:flex-wrap sm:gap-4 sm:justify-between sm:items-center sm:p-4 min-w-0 overflow-x-hidden">
                {/* Search */}
                <div className="relative w-full min-w-0 sm:w-auto sm:min-w-[220px] sm:max-w-xs">
                  <FiSearch className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search trashed deals..."
                    defaultValue={filters.search}
                    onChange={(e) => debouncedSearch(e.target.value)}
                    className="py-2 pr-4 pl-10 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/40 focus:border-[#FF6C6C] transition-colors"
                  />
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {/* Date filter */}
                  <div className="relative trash-date-filter-container">
                    <button
                      ref={dateFilterTriggerRef}
                      type="button"
                      onClick={() => setShowDateFilter((v) => !v)}
                      className={`flex items-center justify-center gap-2 px-3 py-2 sm:px-4 rounded-xl border transition-all duration-200 text-xs sm:text-sm font-medium ${
                        filters.dateFrom || filters.dateTo
                          ? "border-[#CC2E2E] bg-[#FF6C6C]/10 text-[#CC2E2E]"
                          : "border-gray-200 text-gray-600 hover:border-[#FF6C6C]/50 hover:bg-gray-50"
                      }`}
                    >
                      <Calendar className="w-4 h-4 shrink-0" />
                      <span className="whitespace-nowrap">
                        {filters.dateFrom || filters.dateTo ? "Date Filtered" : "Filter by Date"}
                      </span>
                      {(filters.dateFrom || filters.dateTo) && (
                        <X
                          className="w-4 h-4 ml-1 shrink-0 hover:bg-[#FF6C6C]/15 rounded-full p-0.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            clearDateFilter();
                          }}
                        />
                      )}
                    </button>

                    {showDateFilter &&
                      createPortal(
                        <div
                          className="trash-date-filter-portal fixed z-[9999] w-[min(20rem,calc(100vw-2rem))] min-w-0 bg-white rounded-xl border border-gray-200 shadow-xl p-4"
                          style={{
                            top: dateDropdownPosition.top,
                            left: dateDropdownPosition.left,
                          }}
                        >
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-sm font-semibold text-gray-900">
                              Filter by Trashed Date
                            </h3>
                            <button
                              type="button"
                              onClick={() => setShowDateFilter(false)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="space-y-4">
                            <div>
                              <label htmlFor="trash-dateFrom" className="block mb-2 text-sm font-medium text-gray-700">
                                From Date
                              </label>
                              <input
                                type="date"
                                id="trash-dateFrom"
                                max={filters.dateTo || undefined}
                                defaultValue={filters.dateFrom || ""}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/30 focus:border-[#FF6C6C] outline-none text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor="trash-dateTo" className="block mb-2 text-sm font-medium text-gray-700">
                                To Date
                              </label>
                              <input
                                type="date"
                                id="trash-dateTo"
                                min={filters.dateFrom || undefined}
                                defaultValue={filters.dateTo || ""}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/30 focus:border-[#FF6C6C] outline-none text-sm"
                              />
                            </div>
                          </div>
                          <div className="flex justify-between items-center pt-4 mt-6 border-t border-gray-200">
                            <button
                              type="button"
                              onClick={() => {
                                clearDateFilter();
                                setShowDateFilter(false);
                              }}
                              className="text-sm text-gray-600 hover:text-gray-800"
                            >
                              Clear
                            </button>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setShowDateFilter(false)}
                                className="px-4 py-2 text-sm text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const from = document.getElementById("trash-dateFrom")?.value ?? "";
                                  const to = document.getElementById("trash-dateTo")?.value ?? "";
                                  updateDateFilter(from, to);
                                  setShowDateFilter(false);
                                }}
                                className="px-4 py-2 text-sm text-white bg-[#CC2E2E] rounded-lg hover:bg-[#8B1A1A]"
                              >
                                Apply
                              </button>
                            </div>
                          </div>
                        </div>,
                        document.body
                      )}
                  </div>

                  {/* Active filter chip */}
                  {(filters.dateFrom || filters.dateTo || filters.search) && (
                    <button
                      onClick={() => {
                        clearDateFilter();
                        debouncedSearch("");
                      }}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Clear all
                    </button>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[920px]">
                  <thead>
                    <tr className="bg-gray-50/70 border-b border-gray-100">
                      <th className="px-5 py-3.5 text-left">
                        <SortableHeader
                          label="Deal"
                          sortKey="title"
                          currentSortBy={filters.sortBy}
                          currentSortOrder={filters.sortOrder}
                          onSort={handleSort}
                        />
                      </th>
                      <th className="px-5 py-3.5 text-left">
                        <span className="font-semibold text-xs uppercase tracking-wider text-gray-500 whitespace-nowrap flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5 opacity-70" />
                          Vendor
                        </span>
                      </th>
                      <th className="px-5 py-3.5 text-left">
                        <SortableHeader
                          label="Type"
                          sortKey="offer_type"
                          currentSortBy={filters.sortBy}
                          currentSortOrder={filters.sortOrder}
                          onSort={handleSort}
                        />
                      </th>
                      <th className="px-5 py-3.5 text-left">
                        <SortableHeader
                          label="Price"
                          sortKey="price"
                          currentSortBy={filters.sortBy}
                          currentSortOrder={filters.sortOrder}
                          onSort={handleSort}
                        />
                      </th>
                      <th className="px-5 py-3.5 text-left">
                        <span className="font-semibold text-xs uppercase tracking-wider text-gray-500 whitespace-nowrap">
                          Status
                        </span>
                      </th>
                      <th className="px-5 py-3.5 text-center w-16">
                        <span className="font-semibold text-xs uppercase tracking-wider text-gray-500">
                          Actions
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      /* Loading skeleton rows */
                      Array.from({ length: 6 }).map((_, i) => (
                        <tr key={i} className="border-b border-gray-50">
                          {Array.from({ length: 6 }).map((__, j) => (
                            <td key={j} className="px-5 py-4">
                              <div className="h-4 bg-gray-100 rounded-lg animate-pulse" style={{ width: j === 0 ? "80%" : j === 1 ? "70%" : "60%" }} />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : deals.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-20 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FF6C6C]/10 border border-[#FF6C6C]/20">
                              <Inbox className="w-8 h-8 text-[#FF6C6C]/50" />
                            </div>
                            <p className="text-base font-semibold text-gray-500">
                              No trashed deals found
                            </p>
                            <p className="text-sm text-gray-400">
                              {filters.search || filters.dateFrom || filters.dateTo
                                ? "Try adjusting your filters"
                                : "The trash bin is empty"}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <AnimatePresence mode="sync">
                        {deals.map((deal, index) => (
                          <motion.tr
                            key={deal.id ?? deal.deal_slug ?? index}
                            variants={rowVariants}
                            initial="hidden"
                            animate="visible"
                            transition={{ delay: index * 0.03, duration: 0.2 }}
                            className="border-b border-gray-50 hover:bg-[#FF6C6C]/5 transition-colors group"
                          >
                            {/* Deal — image, title (single line), category */}
                            <td className="px-5 py-4 max-w-[280px]">
                              <div className="flex items-start gap-3">
                                {deal.image ? (
                                  <img
                                    src={deal.image}
                                    alt={deal.title}
                                    className="w-12 h-12 rounded-xl object-cover border border-gray-200 shadow-sm shrink-0"
                                    onError={(e) => { e.target.style.display = "none"; }}
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0">
                                    <Inbox className="w-5 h-5 text-gray-400" />
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <p
                                    className="text-sm font-semibold text-gray-800 truncate"
                                    title={deal.title || "Untitled Deal"}
                                  >
                                    {deal.title || "Untitled Deal"}
                                  </p>
                                  {Array.isArray(deal.category) && deal.category.length > 0 && (
                                    <p className="text-[11px] text-gray-500 mt-0.5 truncate">{deal.category[0]}</p>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Vendor */}
                            <VendorCell deal={deal} />

                            {/* Offer type */}
                            <td className="px-5 py-4 whitespace-nowrap">
                              <OfferTypeBadge type={deal.offer_type} />
                            </td>

                            {/* Price — bundle, BOGO, or discount (same logic as Deals table) */}
                            <td className="px-5 py-4 whitespace-nowrap">
                              <div className="text-xs text-gray-900 flex flex-wrap items-baseline gap-x-1.5 gap-y-0">
                                {(() => {
                                  if (deal.bundle && deal.bundle.length > 0 &&
                                      deal.bundle.some((b) => b.original_sum && b.bundle_price)) {
                                    const bundleItem = deal.bundle[0];
                                    return (
                                      <>
                                        <span className="font-medium text-purple-600">{formatCurrency(bundleItem.bundle_price)}</span>
                                        <span className="text-gray-400">·</span>
                                        <span className="text-gray-500">Save {formatCurrency(bundleItem.bundle_savings)}</span>
                                      </>
                                    );
                                  }
                                  if (deal.bogo && deal.bogo.length > 0 &&
                                      deal.bogo.some((b) => b.bogo_item_price || (b.bogo_quantity && b.bogo_free_quantity))) {
                                    const bogoItem = deal.bogo[0];
                                    return (
                                      <>
                                        <span className="font-medium text-blue-600">{formatCurrency(bogoItem.bogo_item_price)}</span>
                                        <span className="text-gray-400">·</span>
                                        <span className="text-gray-500">Buy {bogoItem.bogo_quantity} Get {bogoItem.bogo_free_quantity}</span>
                                      </>
                                    );
                                  }
                                  if (deal.price != null && deal.price !== 0) {
                                    return (
                                      <>
                                        {deal.discounted_price != null && deal.discounted_price !== 0 ? (
                                          <>
                                            <span className="text-gray-400 line-through">{formatCurrency(deal.price)}</span>
                                            <span className="font-medium text-green-600">{formatCurrency(deal.discounted_price)}</span>
                                            {deal.discount_percentage > 0 && (
                                              <span className="text-pink-600">({Math.ceil(deal.discount_percentage)}% off)</span>
                                            )}
                                          </>
                                        ) : (
                                          <span className="font-medium text-gray-600">{formatCurrency(deal.price)}</span>
                                        )}
                                      </>
                                    );
                                  }
                                  return <span className="text-gray-400">—</span>;
                                })()}
                              </div>
                            </td>

                            {/* Status */}
                            <td className="px-5 py-4 whitespace-nowrap">
                              <StatusBadge status={deal.status} />
                            </td>

                            {/* Actions */}
                            <td className="px-5 py-4 text-center">
                              <TrashedActionMenu
                                deal={deal}
                                onView={(d) => setViewModal({ isOpen: true, deal: d })}
                                onRestore={(d) => setRestoreModal({ isOpen: true, deal: d })}
                                onDelete={(d) => setDeleteModal({ isOpen: true, deal: d })}
                              />
                            </td>
                          </motion.tr>
                        ))}
                      </AnimatePresence>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!loading && deals.length > 0 && (
                <div className="flex flex-col gap-3 px-4 py-4 border-t border-gray-100 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-500">
                    Showing{" "}
                    <span className="font-semibold text-gray-700">{indexOfFirstItem + 1}</span>
                    {" – "}
                    <span className="font-semibold text-gray-700">
                      {indexOfFirstItem + deals.length}
                    </span>{" "}
                    of <span className="font-semibold text-gray-700">{totalItems}</span> results
                  </p>

                  <div className="flex items-center gap-3">
                    {/* Items per page */}
                    <select
                      value={itemsPerPage}
                      onChange={(e) => changeItemsPerPage(Number(e.target.value))}
                      className="border border-[#FF6C6C]/30 rounded-lg text-sm text-white py-1.5 px-3 bg-[#CC2E2E] hover:bg-[#8B1A1A] focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/40 transition-colors cursor-pointer"
                    >
                      <option value={5}>5 / page</option>
                      <option value={10}>10 / page</option>
                      <option value={15}>15 / page</option>
                      <option value={20}>20 / page</option>
                      <option value={50}>50 / page</option>
                    </select>

                    {/* Page buttons */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => changePage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg inline-flex items-center justify-center transition-colors ${
                          currentPage === 1
                            ? "text-gray-300 cursor-not-allowed"
                            : "text-gray-500 hover:text-white hover:bg-[#CC2E2E] bg-gray-100"
                        }`}
                      >
                        <FiChevronLeft className="w-4 h-4" />
                      </button>

                      {Array.from({ length: Math.max(1, totalPages) }).map((_, i) => {
                        const page = i + 1;
                        const showPage =
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1);
                        const showEllipsis =
                          page === currentPage - 2 || page === currentPage + 2;
                        if (!showPage && !showEllipsis) return null;
                        if (showEllipsis)
                          return (
                            <span key={i} className="px-1.5 text-gray-400 text-sm">
                              …
                            </span>
                          );
                        return (
                          <button
                            key={i}
                            onClick={() => changePage(page)}
                            className={`min-w-[2rem] h-8 rounded-lg text-sm font-medium transition-colors px-2 ${
                              currentPage === page
                                ? "bg-[#FF6C6C]/15 text-[#CC2E2E] border border-[#FF6C6C]/30"
                                : "text-gray-500 hover:bg-gray-100"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button
                        onClick={() => changePage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg inline-flex items-center justify-center transition-colors ${
                          currentPage === totalPages
                            ? "text-gray-300 cursor-not-allowed"
                            : "text-gray-500 hover:text-white hover:bg-[#CC2E2E] bg-gray-100"
                        }`}
                      >
                        <FiChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Modals */}
      <ViewDealModal
        viewModal={viewModal.isOpen}
        onCloseViewModal={() => setViewModal({ isOpen: false, deal: null })}
        deal={viewModal.deal}
      />
      <RestoreDealModal
        isOpen={restoreModal.isOpen}
        deal={restoreModal.deal}
        onClose={() => setRestoreModal({ isOpen: false, deal: null })}
        onSuccess={refreshData}
      />
      <PermanentDeleteModal
        isOpen={deleteModal.isOpen}
        deal={deleteModal.deal}
        onClose={() => setDeleteModal({ isOpen: false, deal: null })}
        onSuccess={refreshData}
      />
    </div>
  );
};

export default TrashedDeals;
